<template>
  <Dialog
    v-model:visible="showModalRef"
    :close-on-escape="store.getters.dialogsCloseOnEscape"
    :dismissableMask="store.getters.dialogsDismissableMask"
    :modal="store.getters.dialogsModal"
    class="user-group-has-users-dialog large-dialog"
  >
    <template #header>
      Usuarios del grupo
      <i
        class="custom-close-dialog-button cursor-pointer p-2 pi pi-times"
        @click="closeModal()"
      ></i>
    </template>

    <div class="px-2">
      <FindUser
        ref="findUserRef"
        :fromUserGroupHasUsers="true"
        :groupTypeProp="tipoGrupo"
        @addUser="handleAddUser"
        @search-users="handleSearchUsers"
      />

      <div class="mt-3">
        <DataTable
          :value="users"
          :rowClass="rowClass"
          removableSort
          :loading="loading"
          loading-icon="pi pi-spin pi-spinner"
          scrollable
          :scrollHeight="totalHeight"
        >
          <template #empty>
            <div class="p-text-center">
              <span class="p-text-bold">No hay usuarios</span>
            </div>
          </template>
          <Column>
            <template #header>
              <div
                @click="sortBy('nombre')"
                class="flex gap-2 align-items-center cursor-pointer"
              >
                <div>Nombre</div>
                <div v-if="sortField != 'nombre'" class="pi pi-sort-alt"></div>
                <div
                  v-else-if="sortOrder === 1"
                  class="cursor-pointer pi pi-sort-amount-up"
                ></div>
                <div
                  v-else-if="sortOrder === -1"
                  class="cursor-pointer pi pi-sort-amount-down"
                ></div>
              </div>
            </template>
            <template #body="slotProps">
              <div class="long-text-column" style="width: 20rem">
                <span v-if="slotProps.data?.nombre">{{
                  slotProps.data?.nombre
                }}</span>
                <span v-if="slotProps.data?.apellido1"
                  >&nbsp;{{ slotProps.data?.apellido1 }}</span
                >
                <span v-if="slotProps.data?.apellido2"
                  >&nbsp;{{ slotProps.data?.apellido2 }}</span
                >
                <span
                  v-if="
                    !slotProps.data.nombre &&
                    !slotProps.data.apellido1 &&
                    !slotProps.data.apellido2
                  "
                  class="text-gray-400"
                  >-</span
                >
              </div>
            </template>
          </Column>
          <Column field="email">
            <template #header>
              <div
                @click="sortBy('email')"
                class="flex gap-2 align-items-center cursor-pointer"
              >
                <div>Email</div>
                <div v-if="sortField != 'email'" class="pi pi-sort-alt"></div>
                <div
                  v-else-if="sortOrder === 1"
                  class="cursor-pointer pi pi-sort-amount-up"
                ></div>
                <div
                  v-else-if="sortOrder === -1"
                  class="cursor-pointer pi pi-sort-amount-down"
                ></div>
              </div>
            </template>
          </Column>
          <Column v-if="showActionsColumn" header="Acciones">
            <template #body="slotProps">
              <Button
                @click="deleteUser(slotProps.data)"
                :icon="[
                  deletingUserEmail == slotProps.data.email
                    ? 'pi pi-spin pi-spinner'
                    : 'pi pi-trash',
                ]"
                severity="danger"
              />
            </template>
          </Column>
        </DataTable>
        <Paginator
          :rows="rows"
          :totalRecords="totalResults"
          :rowsPerPageOptions="rowsPerPageOptions"
          @page="onPage"
        ></Paginator>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, nextTick, onMounted, computed } from "vue";
import Dialog from "primevue/dialog";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Paginator from "primevue/paginator";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { useStore } from "vuex";
import { useAuthorization } from "@sttedra_ui/framework";

import FindUser from "@/components/partials/FindUser.vue";
import {
  appUserGroupAddUser,
  appUserGroupDeleteUser,
} from "@/api/users-groups/users-groups.api.js";
import { appUserFindByGroup } from "@/api/users/users.api";

// #region Props, Emits
const emit = defineEmits(["handleUpdateTableList"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const { modelRef: authorizationModelRef } = useAuthorization();
const userRol = authorizationModelRef.value.roles[0];
const deletingUserEmail = ref(null);
const findUserRef = ref(null);
const store = useStore();
const showModalRef = ref(false);
const users = ref([]);
const idGrupo = ref(null);
const grupo = ref(null);
const tipoGrupo = ref(null);
const confirm = useConfirm();
const toast = useToast();
const loading = ref(false);
const addUser = ref(null);

const sortField = ref(null);
const sortOrder = ref(null);
const pageIndex = ref(1);
const pageSize = ref(10);
const rows = ref(10);
const rowsPerPageOptions = ref([10, 20, 50]);
const totalResults = ref(0);
const search = ref("");
const totalHeight = ref("100vh");
const selectedImpersonation = ref(null);

const showActionsColumn = computed(() => {
  if (selectedImpersonation.value) {
    return selectedImpersonation.value.esAdministrador;
  }

  return tipoGrupo.value === 0 && userRol === "Admin";
});
// #endregion

// #region Functions
// Open the modal with the data
async function showModal(data) {
  grupo.value = data;
  idGrupo.value = data.id;
  showModalRef.value = true;

  if (data.tipo != null && data.tipo != undefined) {
    tipoGrupo.value = data.tipo;
  } else {
    if (data.tipoGrupo == "Local") {
      tipoGrupo.value = 0; // Local
    } else if (data.tipoGrupo == "Directorio Activo") {
      tipoGrupo.value = 1; // AD
    } else if (data.tipoGrupo == "UPRUC") {
      tipoGrupo.value = 2; // UPRUC
    }
  }

  await nextTick();

  onPage();
  setTableHeight();
}

async function handleAddUser(user) {
  if (userRol != "Admin") return;

  findUserRef.value.loading = true;
  addUser.value = user;

  const response = await appUserGroupAddUser({
    idGrupo: idGrupo.value,
    email: user.email,
  });

  if (response.status == 200) {
    toast.add({
      severity: "success",
      summary: "Usuario asociado",
      detail: `Se ha asociado el usuario al grupo correctamente`,
      life: 3000,
    });

    onPage();
  } else {
    const error = response.response.data?.errors?.[0]?.message;

    let errorMessage = `Ha ocurrido un error al añadir el usuario`;

    if (error) {
      errorMessage = error;
    }

    toast.add({
      severity: "error",
      summary: "Error",
      detail: errorMessage,
      life: 3000,
    });
  }

  findUserRef.value.loading = false;
}

function deleteUser(user) {
  confirm.require({
    message: "¿Desea borrar el registro?",
    header: "Borrar",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      deletingUserEmail.value = user.email;

      // If the user accept the confirm, delete the register
      const response = await appUserGroupDeleteUser({
        idGrupo: idGrupo.value,
        email: user.email,
      });

      // If the response is 200, show a success toast, else show a error toast
      if (response.status == 200) {
        toast.add({
          severity: "success",
          summary: "Registro borrado",
          detail: `Se ha borrado el registro correctamente`,
          life: 3000,
        });

        onPage();
      } else {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Ha ocurrido un error al borrar el registro",
          life: 3000,
        });
      }

      deletingUserEmail.value = null;
    },
  });
}

function closeModal() {
  addUser.value = null;
  showModalRef.value = false;
  users.value = [];
  
  // Resetear los filtros y estado de búsqueda
  search.value = "";
  sortField.value = null;
  sortOrder.value = null;
  pageIndex.value = 1;
  pageSize.value = 10;
  rows.value = 10;
}

function handleSearchUsers(query) {
  search.value = query;
  onPage();
}

function sortBy(field) {
  sortField.value = field;

  switch (sortOrder.value) {
    case null:
      sortOrder.value = 1;
      break;

    case 1:
      sortOrder.value = -1;
      break;

    case -1:
      sortOrder.value = null;
      sortField.value = null;
      break;
  }

  onPage();
}

async function onPage(event = null) {
  loading.value = true;

  if (event) {
    rows.value = event.rows;
    pageIndex.value = event.page + 1;
    pageSize.value = event.rows;
  }

  let sortOrderFormatted = null;

  switch (sortOrder.value) {
    case 1:
      sortOrderFormatted = "asc";
      break;
    case -1:
      sortOrderFormatted = "desc";
      break;
    default:
      sortOrderFormatted = null;
  }

  const response = await appUserFindByGroup({
    idGrupo: idGrupo.value,
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    sortField: sortField.value,
    sortOrder: sortOrderFormatted,
    GLOBALSEARCH: {
      VALUE: search.value,
      FIELDS: ["Nombre", "Email"],
    },
  });

  if (response.status == 200) {
    users.value = response.data.data;
    totalResults.value = response.data.totalResults;
  }

  loading.value = false;
  emit("handleUpdateTableList");
}

function rowClass(data) {
  return [{ "blink-row": addUser.value && data.email == addUser.value.email }];
}

async function setTableHeight() {
  const dialog = document.querySelector(".user-group-has-users-dialog");

  if (!dialog) return;

  await nextTick();

  const dialogHeader = dialog.querySelector(".p-dialog-header");
  const datatableTHead = dialog.querySelector(".p-datatable-thead");
  const paginatorWrapper = dialog.querySelector(".p-paginator");
  const inputGroup = dialog.querySelector(".p-inputgroup");

  const dialogHeight = dialog ? dialog.clientHeight : 0;
  const dialogHeaderHeight = dialogHeader ? dialogHeader.clientHeight : 0;
  const datatableTHeadHeight = datatableTHead ? datatableTHead.clientHeight : 0;
  const paginatorWrapperHeight = paginatorWrapper
    ? paginatorWrapper.clientHeight
    : 0;
  const inputGroupHeight = inputGroup ? inputGroup.clientHeight : 0;

  const maxTableHeight =
    dialogHeight -
    dialogHeaderHeight -
    datatableTHeadHeight -
    paginatorWrapperHeight -
    inputGroupHeight;

  totalHeight.value = `calc(${maxTableHeight}px)`;
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    selectedImpersonation.value = JSON.parse(impersonationToken);
  }
});
// #endregion

// #region Provide, Expose
defineExpose({
  showModal,
});
// #endregion
</script>
